import { __decorate } from "tslib";
import React, { Component } from 'react';
import { observer } from 'mobx-react';
import { action } from 'mobx';
import { Draggable, Droppable } from 'react-beautiful-dnd';
import { pxToRem } from 'choerodon-ui/lib/_util/UnitConvertor';
import ReactResizeObserver from 'choerodon-ui/lib/_util/resizeObserver';
import { getConfig } from 'choerodon-ui/lib/configure';
import isFunction from 'lodash/isFunction';
import TableContext from './TableContext';
import TableRow from './TableRow';
import { ColumnLock, DragColumnAlign } from './enum';
import ExpandedRow from './ExpandedRow';
import { DataSetStatus } from '../data-set/enum';
import autobind from '../_util/autobind';
import { instance } from './Table';
import { isDraggingStyle, isStickySupport } from './utils';
let TableTBody = class TableTBody extends Component {
    constructor(props, context) {
        super(props, context);
        const { tableStore, dataSet } = context;
        if (tableStore.performanceEnabled) {
            if (dataSet.status === DataSetStatus.ready && dataSet.length) {
                tableStore.performanceOn = true;
                tableStore.timing.renderStart = Date.now();
            }
        }
    }
    handlePerformance() {
        const { code, tableStore, dataSet } = this.context;
        if (tableStore.performanceEnabled && tableStore.performanceOn) {
            const { timing } = tableStore;
            const { performance } = dataSet;
            const onPerformance = getConfig('onPerformance');
            timing.renderEnd = Date.now();
            onPerformance('Table', {
                name: code,
                url: performance.url,
                size: dataSet.length,
                timing: {
                    ...performance.timing,
                    ...timing,
                },
            });
            tableStore.performanceOn = false;
        }
    }
    componentDidMount() {
        this.handlePerformance();
    }
    componentWillUpdate() {
        const { tableStore } = this.context;
        if (tableStore.performanceEnabled && tableStore.performanceOn) {
            tableStore.timing.renderStart = Date.now();
        }
    }
    componentDidUpdate() {
        this.handlePerformance();
        const { lock } = this.props;
        if (!lock) {
            const { tableStore: { node }, } = this.context;
            if (node.isFocus &&
                !node.wrapper.contains(document.activeElement)) {
                node.focus();
            }
        }
    }
    handleResize(_width, height) {
        const { tableStore } = this.context;
        if (!tableStore.hidden) {
            tableStore.calcBodyHeight = height;
        }
    }
    render() {
        const { lock, columnGroups } = this.props;
        const { prefixCls, tableStore, rowDragRender, dataSet, } = this.context;
        const { data, virtual, rowDraggable, } = tableStore;
        const virtualData = virtual ? data.slice(tableStore.virtualStartIndex, tableStore.virtualEndIndex) : data;
        const rows = virtualData.length
            ? this.getRows(virtualData, columnGroups, true, virtual)
            : this.getEmptyRow(columnGroups);
        const body = rowDraggable ? (React.createElement(Droppable, Object.assign({ droppableId: "table", key: "table", renderClone: (provided, snapshot, rubric) => {
                if (snapshot.isDragging && tableStore.overflowX && tableStore.dragColumnAlign === DragColumnAlign.right) {
                    const { style } = provided.draggableProps;
                    if (isDraggingStyle(style)) {
                        const { left, width } = style;
                        style.left = left - Math.max(tableStore.columnGroups.leafColumnsWidth - 50, width);
                    }
                }
                const record = dataSet.get(rubric.source.index);
                if (record) {
                    const leafColumnsBody = lock ? tableStore.columnGroups : columnGroups;
                    const renderClone = rowDragRender && rowDragRender.renderClone;
                    const { id } = record;
                    if (renderClone && isFunction(renderClone)) {
                        return renderClone({
                            provided,
                            snapshot,
                            rubric,
                            key: id,
                            hidden: false,
                            lock: false,
                            prefixCls,
                            columns: leafColumnsBody.leafs.map(({ column }) => column),
                            columnGroups: leafColumnsBody,
                            record,
                            index: id,
                        });
                    }
                    return (React.createElement(TableRow, { provided: provided, snapshot: snapshot, key: id, hidden: false, lock: false, columnGroups: leafColumnsBody, record: record, index: id }));
                }
                return React.createElement("span", null);
            }, getContainerForClone: () => instance(tableStore.node.getClassName(), prefixCls).tbody }, (rowDragRender && rowDragRender.droppableProps)), (droppableProvided) => (React.createElement("tbody", Object.assign({ ref: droppableProvided.innerRef }, droppableProvided.droppableProps, { className: `${prefixCls}-tbody` }),
            rows,
            droppableProvided.placeholder)))) : (React.createElement("tbody", { className: `${prefixCls}-tbody` }, rows));
        return lock || virtual ? (body) : (React.createElement(ReactResizeObserver, { onResize: this.handleResize, resizeProp: "height", immediately: true }, body));
    }
    getRows(records, columnGroups, expanded, virtual) {
        return records.map((record, index) => this.getRow(columnGroups, record, virtual ? record.index : index, expanded));
    }
    getEmptyRow(columnGroups) {
        const { prefixCls, dataSet, tableStore: { emptyText, width }, } = this.context;
        const { lock } = this.props;
        const styles = width ? {
            position: isStickySupport() ? 'sticky' : 'relative',
            left: pxToRem(width / 2),
        } : {
            transform: 'none',
            display: 'inline-block',
        };
        const tdStyle = width ? undefined : { textAlign: 'center' };
        return (React.createElement("tr", { className: `${prefixCls}-empty-row` },
            React.createElement("td", { colSpan: columnGroups.leafs.length, style: tdStyle },
                React.createElement("div", { style: styles }, !lock && dataSet.status === DataSetStatus.ready && emptyText))));
    }
    renderExpandedRows(columnGroups, record, isExpanded) {
        return this.getRows(record.children || [], columnGroups, isExpanded);
    }
    getRow(columnGroups, record, index, expanded) {
        const { lock } = this.props;
        const { tableStore, rowDragRender } = this.context;
        const { key } = record;
        const children = tableStore.isTree && (React.createElement(ExpandedRow, { record: record, columnGroups: columnGroups }, this.renderExpandedRows));
        if (tableStore.rowDraggable) {
            const { dragColumnAlign } = tableStore;
            if (!dragColumnAlign || (dragColumnAlign === DragColumnAlign.right && lock !== ColumnLock.left) || (dragColumnAlign === DragColumnAlign.left && lock !== ColumnLock.right)) {
                return (React.createElement(Draggable, { draggableId: String(key), index: index, key: record.key }, (provided, snapshot) => (React.createElement(TableRow, Object.assign({ provided: provided, snapshot: snapshot, key: record.key, hidden: !expanded, lock: lock, columnGroups: columnGroups, record: record, index: index }, (rowDragRender && rowDragRender.draggableProps)), children))));
            }
        }
        return (React.createElement(TableRow, { key: key, hidden: !expanded, lock: lock, columnGroups: columnGroups, record: record, index: index }, children));
    }
};
TableTBody.displayName = 'TableTBody';
TableTBody.contextType = TableContext;
__decorate([
    autobind,
    action
], TableTBody.prototype, "handleResize", null);
__decorate([
    autobind
], TableTBody.prototype, "renderExpandedRows", null);
TableTBody = __decorate([
    observer
], TableTBody);
export default TableTBody;
